import { Breadcrumb, BreadcrumbItem } from '@/components/Breadcrumb';
import {
  buildJobsListingCountLabel,
  buildJobsListingDescription,
  buildJobsListingHeading,
} from '@/utils/jobListingHelper';
import cx from 'classnames';

import styles from './PageHeader.module.scss';

export type PageHeaderProps = {
  breadcrumbItems: BreadcrumbItem[];
  grade?: string;
  speciality?: string;
  location?: string;
  total: number;
  state?: string;
};

type StatVariant = 'fullWidth' | 'outline' | 'solid';

const STATS: { value: string; description: string; variant: StatVariant }[] = [
  { value: '3x more jobs', description: 'than the next agency', variant: 'fullWidth' },
  { value: '20+', description: 'years industry experience', variant: 'outline' },
  { value: '5000+', description: 'doctors placed every year', variant: 'solid' },
];

export const PageHeader = ({
  breadcrumbItems,
  grade,
  speciality,
  location,
  total,
  state,
}: PageHeaderProps) => {
  const heading = buildJobsListingHeading({ grade, speciality, location, state });
  const countLabel = buildJobsListingCountLabel(total, speciality, grade);
  const description = buildJobsListingDescription({ grade, location });

  return (
    <div className={styles.pageHeader} data-testid="jobs-listing-page-header">
      <div className={styles.content}>
        <div className={styles.mainColumn}>
          <Breadcrumb items={breadcrumbItems} className={styles.breadcrumb} />

          <div className={styles.headingGroup}>
            {countLabel && (
              <div className={styles.countPill} data-testid="jobs-listing-count-pill">
                <span className={styles.countDot} />
                {countLabel}
              </div>
            )}

            <h1 className={styles.heading}>
              {heading.gradeLine && <span className={styles.gradeLine}>{heading.gradeLine}</span>}
              <span className={styles.mainLine}>
                {heading.prefix}
                {heading.accent && (
                  <>
                    {' '}
                    <span className={styles.accent}>{heading.accent}</span>
                  </>
                )}
              </span>
            </h1>

            <p className={styles.description}>{description}</p>
          </div>
        </div>

        <div className={styles.stats} data-testid="jobs-listing-stats">
          {STATS.map(stat => (
            <div
              key={stat.value}
              className={cx(styles.stat, {
                [styles.statFullWidth]: stat.variant === 'fullWidth',
                [styles.statSolid]: stat.variant === 'solid',
              })}
            >
              <p
                className={cx(styles.statValue, {
                  [styles.statValueAccent]: stat.variant === 'solid',
                })}
              >
                {stat.value}
              </p>
              <p className={styles.statDescription}>{stat.description}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PageHeader;
